// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ContactNoteSearchPageCmp, ContactNoteSearchPageProps } from "./types";
import { CONTACT_NOTE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import {
  Routes,
} from "ui/modules/contacts/navigation";
import TabMenu from "ui/modules/contacts/pages/components/TabMenu/TabMenu";

// Lokalne komponente
import ContactNoteSearchResults from "./components/ContactNoteSearchResults/ContactNoteSearchResults";
import ContactNoteSearchFilters from "./components/ContactNoteSearchFilters/ContactNoteSearchFilters";
import {
  ContactDetailFormValues,
  ContactNoteRow,
  ContactNoteSearchFormValues,
} from "services/api/contacts/types";
import { deleteContactNoteAPI, getContactAPI, getContactNotesAPI } from "services/api/contacts/api";
import ContactNoteEditCard from "../ContactNoteEditCard/ContactNoteEditCard";
import { Dialog } from "ui/components/Dialog";
import { useTranslate } from "helpers";
import { initialContactDetailForm } from "services/api/contacts/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";

const ContactNoteSearchPage: ContactNoteSearchPageCmp = (
  props: ContactNoteSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactNoteRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<ContactDetailFormValues>(
    initialContactDetailForm
  );

  const [noteId, setNoteId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSearchResult(searchResults.pagination, id);
      fetchContact(id);
    }
  }, [id]);

  const fetchContact = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialContactDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      if (id) {
        const results = await getContactNotesAPI(pagination, id, name);

        setSearchResults(results);
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setNoteId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setNoteId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: ContactNoteSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setShowAddNewModal(false);
  }, []);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setNoteId(id);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!noteId) return;

    try {
      if (id) {
        const deleted = await deleteContactNoteAPI(
          t,
          id,
          noteId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, id);
        }
      }

      setOpenDialog(false);
      setNoteId("");
    } catch (err) {
      console.error("Greška prilikom brisanja bilješke:", err);
    }
  }, [noteId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setNoteId("");
    setOpenDialog(false);
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];



  if (!id) return <>REDIRECT TODO</>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <PageTitle
                      title={`${t(CONTACT_NOTE_SEARCH_PAGE_TITLE.text)} [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                      icon={CONTACT_NOTE_SEARCH_PAGE_TITLE.icon}
                      iconColor={CONTACT_NOTE_SEARCH_PAGE_TITLE.iconColor}
                    />
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-4 py-4">
          <ContactNoteSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite bilješku klikom na 'Dodaj bilješku'" />
          ) : (
            <ContactNoteSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <ContactNoteEditCard
        noteId={noteId}
        contactId={id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t("admin.contact.note.dialog.delete.title")}
        message={t("admin.contact.note.dialog.delete.message")}
        confirmText={t("admin.contact.note.dialog.delete.confirmtext")}
        cancelText={t("admin.contact.note.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

ContactNoteSearchPage.route = Routes.ContactNoteSearchPageRouter;

export default ContactNoteSearchPage;
